import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Clock, LogOut, Smartphone, Monitor, Tablet, HelpCircle } from "lucide-react";
import { useAuth, LAST_ACTIVE_STORAGE_KEY } from "@/lib/auth";
import { api } from "@/lib/rn3";
import { useT } from "@/lib/i18n";

const DeviceIcon = ({ device }) => {
  if (device === "mobile") return <Smartphone className="w-4 h-4" />;
  if (device === "tablet") return <Tablet className="w-4 h-4" />;
  if (device === "desktop") return <Monitor className="w-4 h-4" />;
  return <HelpCircle className="w-4 h-4" />;
};

// Session card: "Last active" relative time, recent sign-ins device history,
// and "Sign out everywhere" button. Works for both client and admin users.
const SessionCard = ({ className = "" }) => {
  const t = useT();
  const { signOutEverywhere } = useAuth();
  const [lastActive, setLastActive] = useState(() => {
    try { return parseInt(localStorage.getItem(LAST_ACTIVE_STORAGE_KEY) || "0", 10) || Date.now(); }
    catch { return Date.now(); }
  });
  const [busy, setBusy] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);

  // Poll the localStorage timestamp every 30s so the "Last active X min ago" label
  // ticks in near-real-time without re-rendering on every mouse move.
  useEffect(() => {
    const id = setInterval(() => {
      try {
        const v = parseInt(localStorage.getItem(LAST_ACTIVE_STORAGE_KEY) || "0", 10);
        if (v) setLastActive(v);
      } catch { /* noop */ }
    }, 30 * 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    api.get("/users/me/sessions")
      .then(({ data }) => setSessions(data.items || []))
      .catch(() => setSessions([]))
      .finally(() => setSessionsLoading(false));
  }, []);

  const relative = (ts) => {
    const tsNum = typeof ts === "number" ? ts : new Date(ts).getTime();
    const diff = Math.max(0, Date.now() - tsNum);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t("session.never");
    if (mins < 60) return t("session.minutes_ago").replace("{n}", String(mins));
    const hrs = Math.floor(mins / 60);
    return t("session.hours_ago").replace("{n}", String(hrs));
  };

  const handleSignOutEverywhere = async () => {
    if (!window.confirm(t("session.sign_out_everywhere_confirm"))) return;
    setBusy(true);
    try {
      await signOutEverywhere();
      toast.success(t("session.signed_out_everywhere"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`rn3-card ${className}`} data-testid="session-card">
      <div className="rn3-eyebrow flex items-center gap-2"><Clock className="w-4 h-4" /> {t("session.last_active")}</div>
      <h3 className="font-serif-display text-2xl mt-2" data-testid="session-last-active">{relative(lastActive)}</h3>
      <div className="border-t border-[#C4A252] w-10 mt-3 mb-4" />

      <div className="mb-5">
        <div className="rn3-eyebrow mb-2">{t("session.recent_signins")}</div>
        <p className="text-xs text-[#737373] mb-3">{t("session.recent_signins_hint")}</p>
        {sessionsLoading ? (
          <div className="text-sm text-[#737373]">{t("common.loading")}</div>
        ) : sessions.length === 0 ? (
          <div className="text-sm text-[#737373]">{t("session.no_sessions")}</div>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="recent-signins-list">
            {sessions.map((s, idx) => (
              <div
                key={`${s.ip_address}|${s.label}|${idx}`}
                className={`flex items-center gap-3 p-3 border ${s.current ? "border-[#C4A252] bg-[#FDFBF7]" : "border-[#E5E5E5]"}`}
                data-testid={s.current ? "session-row-current" : "session-row"}
              >
                <div className="text-[#8B1C22]"><DeviceIcon device={s.device} /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                    <span>{s.label}</span>
                    {s.current && (
                      <span className="inline-block text-[10px] uppercase tracking-wider bg-[#C4A252] text-white px-1.5 py-0.5">
                        {t("session.current_session")}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#737373]">
                    IP: {s.ip_address || "—"} · {relative(s.last_seen)} · {t("session.times_seen").replace("{n}", String(s.count))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-sm text-[#737373] mb-4">{t("session.sign_out_everywhere_hint")}</p>
      <button
        type="button"
        onClick={handleSignOutEverywhere}
        disabled={busy}
        data-testid="sign-out-everywhere-btn"
        className="inline-flex items-center gap-2 px-4 py-2 border border-[#8B1C22] text-[#8B1C22] hover:bg-[#8B1C22] hover:text-white transition-colors text-sm font-medium tracking-wide disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <LogOut className="w-4 h-4" />
        {busy ? t("common.loading") : t("session.sign_out_everywhere")}
      </button>
    </div>
  );
};

export default SessionCard;
